<template>
  <div class="join">
    <booking-header-details
      ref="subheader"
      @edit="$emit('edit-query', $event)"
    />
    <div
      class="join-wrapper"
      data-component="booking-fans-join-wrapper"
      v-if="!isTravelAgent"
    >
      <div class="join-content">
        <div
          class="join-details"
          :class="{'--padding-top-lg': showColumns}"
        >
          <div class="join-details-header">
            {{ title }}
          </div>
          <div
            class="join-details-description"
            v-html="description"
          />
          <div class="join-details-ctas">
            <auth-activator>
              <template slot-scope="{ activate }">
                <button
                  class="log-in"
                  @click.prevent="activate('login', $event.target)"
                >
                  {{ logInButtonLabel }}
                </button>
                <button
                  class="sign-up"
                  @click.prevent="activate('registration', $event.target)"
                >
                  {{ signUpButtonLabel }}
                </button>
              </template>
            </auth-activator>
          </div>
        </div>
        <div
          v-if="showCarousel"
          class="join-privileges-carousel"
        >
          <carousel
            :key="complimentaryPrivileges.length"
            selector=".carousel-wrapper"
            variant="complimentary-privileges"
          >
            <section
              slot-scope="{ imageLoadedEvent, canLoad }"
              class="carousel"
            >
              <div class="carousel-wrapper">
                <div
                  v-for="(
                    privilege, index
                  ) in complimentaryPrivileges"
                  :key="`privilege-${index}`"
                  class="slide"
                >
                  <div class="slide-content">
                    <picture
                      v-lazyload="{
                        src: privilege.IconUrl,
                        set: [
                          { width: '480', height: '240', breakpoint: 'xs' },
                          { width: '800', height: '360', breakpoint: 'md' },
                        ],
                        fit: 'crop',
                        align: 'center',
                        updateCallback: imageLoadedEvent,
                        canLoad,
                      }"
                      class="image-wrapper"
                    >
                      <img
                        class="image"
                        src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                        :alt="privilege.Description"
                      >
                    </picture>
                    <div class="slide-description">
                      {{ privilege.Description }}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </carousel>
        </div>
        <div
          v-if="showColumns"
          class="join-privileges-columns"
        >
          <div
            v-for="(column, columnIndex) in privilegesColumns"
            :key="columnIndex"
            class="join-privileges-columns__column"
          >
            <div
              v-for="(item, itemIndex) in column"
              :key="itemIndex"
              class="join-privileges-columns__card"
            >
              <img
                class="join-privileges-columns__card-image"
                :src="item.IconUrl"
                :alt="item.Description"
              >
              <div class="join-privileges-columns__card-title">
                {{ item.Description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <booking-add-ons v-if="showAddOns" />
  </div>
</template>

<script>
  import $ from 'jquery'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import BookingHeaderDetails from '../../components/booking-header-details/booking-header-details.vue'
  import BookingAddOns from '../../components/booking-add-ons/booking-add-ons'

  export default {
    name: 'JoinFans',
    components: {
      BookingHeaderDetails,
      BookingAddOns
    },
    mixins: [Tracking],
    inject: ['addOnsFeatureEnabled'],
    beforeRouteEnter(to, from, next) {
      next(async (vm) => {
        if (vm.isTravelAgent && !vm.showAddOns) {
          vm.nextStep('replace')
        } else if (vm.userId) {
          if (vm.bookingReady) {
            if (from.name === 'enhancements') {
              vm.prevStep('replace')
            } else {
              vm.nextStep('replace')
            }
          } else {
            vm.pushDataLayer()
            return true
          }
        } else {
          await vm.updateFooterLabel()
          vm.pushDataLayer()
          return true
        }
      })
    },
    data: () => ({
      identifier: null,
      password: null,
      isShow: false,
      scrollHeight: 'auto',
    }),
    computed: {
      ...mapState('global', ['isFanClubBooking']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('auth', ['userId']),
      ...mapGetters('cart', ['getValidAddOnsByRoomId', 'addOnsInRoom']),
      ...mapGetters('booking', ['bookingFlow']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapState('auth', [
        'isLoginPending',
        'authErrors',
        'complimentaryPrivilegesComponentData',
      ]),
      ...mapState('booking', { bookingReady: 'ready', query: 'query', currentRoomId: 'currentRoomId' }),
      ...mapState('responsive', { wHeight: 'height', wWidth: 'width', check: 'check' }),
      serverErrorMsg() {
        return this.authErrors[0]?.message
      },
      isTravelAgent() {
        return !!this.query.iataNumber || this.isFanClubBooking
      },
      title() {
        return this.complimentaryPrivilegesComponentData?.Heading
      },
      description() {
        return this.complimentaryPrivilegesComponentData?.Description
      },
      signUpButtonLabel() {
        return this.complimentaryPrivilegesComponentData?.SignUpButtonLabel
      },
      logInButtonLabel() {
        return this.complimentaryPrivilegesComponentData?.LogInButtonLabel
      },
      complimentaryPrivileges() {
        return this.complimentaryPrivilegesComponentData?.ComplimentaryPrivileges ?? []
      },
      isMobile() {
        return this.check.isLgAndDown
      },
      showAddOns() {
        return this.addOnsFeatureEnabled && this.bookingReady && this.getValidAddOnsByRoomId(this.currentRoomId).length > 0
      },
      showCarousel() {
        return this.isMobile || this.showAddOns
      },
      showColumns() {
        return !this.isMobile && !this.showAddOns
      },
      privilegesColumns() {
        const midpoint = Math.ceil(this.complimentaryPrivileges.length / 2)
        return [
          this.complimentaryPrivileges.slice(0, midpoint),
          this.complimentaryPrivileges.slice(midpoint)
        ]
      },
      nextStepIndex() {
        return this.query.roomQuantity > 1 ? 'privileges-overview' : 3
      },
    },
    watch: {
      userId(val) {
        if (val) {
          this.nextStep('replace')
        }
      },
      bookingReady(val) {
        if (this.userId && val) {
          this.nextStep('replace')
        } else if (this.bookingReady && val) {
          this.validateEnhancements()
          this.updateFooterLabel()
          this.calculateHeight()
        }
      },
      wWidth(val) {
        this.$nextTick(() => {
          this.calculateHeight()
        })
      },
      addOnsInRoom() {
        this.updateFooterLabel()
      },
    },
    async mounted() {
      if (this.bookingReady) {
        this.calculateHeight()
        await this.validateEnhancements()
      }
    },
    methods: {
      ...mapActions('booking', ['nextStep', 'prevStep', 'updateNextStepButton']),
      ...mapActions('auth', ['login']),
      ...mapActions('cart', ['validateEnhancements']),
      async submit() {
        const form = $(this.$refs.form)
        if (!form.valid()) {
          return
        }
        try {
          await this.login({
            username: this.identifier,
            password: this.password,
          })
        } catch (err) {
          console.log(err)
        }
      },
      async updateFooterLabel() {
        if (this.addOnsInRoom.length > 0) {
          const label = this.isBookingEditMode
            ? `${this.tc('booking.continueTo')
            } ${
              this.tc('booking.guestDetails')}`
            : `${this.tc('booking.continueTo')} ${this.tc('steps.checkOut')}`
          this.updateNextStepButton({
            label,
            step: this.nextStepIndex,
            customClass: 'continue',
          })
        } else {
          const label = this.isBookingEditMode
            ? `${this.tc('booking.skipTo')} ${this.tc('booking.guestDetails')}`
            : `${this.tc('booking.skipTo')} ${this.tc('steps.checkOut')}`
          this.updateNextStepButton({
            label,
            step: this.nextStepIndex,
            customClass: '',
          })
        }
      },
      calculateHeight() {
        const headerHeight = document
          .querySelector('header.booking-header')
          .getBoundingClientRect().height
        const footerHeight = document.querySelector('.booking-footer')?.getBoundingClientRect()
          .height ?? 112
        const subheaderHeight = this.$refs.subheader?.$el?.getBoundingClientRect().height ?? 74
        this.scrollHeight = this.wHeight - headerHeight - subheaderHeight - footerHeight
      },
      pushDataLayer() {
        this.$track(this.getTrackingData(this.isBookingEditMode ? 'modifyReservation' : 'booking', undefined, {
          event: 'bookingStateChange',
          bookingEngine: {
            bookingStepNumber: this.isBookingEditMode ? '2.1' : '3.1',
            bookingStepName: 'Enhancements Login',
            bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
            bookingFlow: this.bookingFlow()
          }
        }))
      }
    },
  }
</script>
